import { useEffect } from "react"
import { Button, CardBody, Chip, Skeleton } from "@heroui/react"
import { Column, Row, ScrollableList } from "../components/Layout"
import Card from "../components/Card"
import TextBanner from "../components/typography/TextBanner"
import TextHero from "../components/typography/TextHero"
import TextLabel from "../components/typography/TextLabel"
import TextSubtitle from "../components/typography/TextSubtitle"
import useHealthAPI from "../hooks/useHealthAPI"

function HealthCounter({ label, value, loading, color }: {
    label: string,
    value: number | null,
    loading: boolean,
    color?: 'default' | 'success' | 'danger'
}) {
    const text = value != null ? value : "-"

    return (
        <Column className="w-4/12" alignItems={"center"}>
            <TextLabel>{label}</TextLabel>
            <Skeleton className="rounded-lg" isLoaded={!loading}>
                <Chip color={color ?? 'default'} variant={color && color !== 'default' ? 'solid' : 'flat'} radius="full">
                    <TextHero>{text}</TextHero>
                </Chip>
            </Skeleton>
        </Column>
    )
}

export default function Health() {

    const { loading, error, scannerHealth, fetchScannerHealth } = useHealthAPI();

    useEffect(() => {
        const regionSelector = document.getElementsByClassName('custom-region-selector')[0]
        regionSelector?.remove()
    }, [])

    const missingCount = scannerHealth?.keysWithoutSummaryCount ?? null
    const totalKeys = scannerHealth?.keysTotal ?? null
    const scannedCount = (totalKeys != null && missingCount != null) ? totalKeys - missingCount : null

    const hasMissing = missingCount != null && missingCount > 0

    const dateText = scannerHealth?.dateUtc
        ? new Date(scannerHealth.dateUtc).toLocaleDateString()
        : null

    return <Row expand>
        <Column className='w-3/12' expand>
            <Card className="h-full" fullWidth>
                <CardBody>
                    <Column>
                        <TextBanner>
                            Health
                        </TextBanner>
                        <TextSubtitle>An overview of the system health.</TextSubtitle>
                    </Column>
                </CardBody>
            </Card>
        </Column>
        <Column className='w-9/12' expand>
            <Card className="h-full" fullWidth>
                <CardBody>
                    <Column gap={2} fullWidth>
                        <Row alignItems={"center"}>
                            <Column expand>
                                <TextBanner>Scanners</TextBanner>
                                {dateText && <TextLabel>Coverage for {dateText}</TextLabel>}
                            </Column>
                            <Button
                                size="sm"
                                variant="flat"
                                isLoading={loading}
                                onPress={() => fetchScannerHealth()}
                            >
                                Refresh
                            </Button>
                        </Row>

                        {error && (
                            <Chip color="danger" variant="flat">{error}</Chip>
                        )}

                        {!error && (
                            <>
                                <Row>
                                    <HealthCounter label="Total" value={totalKeys} loading={loading} />
                                    <HealthCounter label="Scanned" value={scannedCount} loading={loading} color="success" />
                                    <HealthCounter label="Missing" value={missingCount} loading={loading} color={hasMissing ? "danger" : "default"} />
                                </Row>

                                {!loading && scannerHealth != null && (
                                    hasMissing ? (
                                        <Column gap={1} fullWidth>
                                            <TextLabel>Scanners without a summary today</TextLabel>
                                            <ScrollableList
                                                maxHeight="60vh"
                                                fullWidth
                                                items={scannerHealth.keysWithoutSummary.map((key) => (
                                                    <Card key={key} fullWidth>
                                                        <CardBody>
                                                            <Row alignItems={"center"}>
                                                                <Column expand>{key}</Column>
                                                                <Chip size="sm" color="danger" >No summary</Chip>
                                                            </Row>
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                            />
                                        </Column>
                                    ) : (
                                        <Row mainAxisCenter>
                                            <Chip color="success">
                                                All scanners reported a summary today.
                                            </Chip>
                                        </Row>
                                    )
                                )}
                            </>
                        )}
                    </Column>
                </CardBody>
            </Card>
        </Column>
    </Row>
}
